import { useState } from 'react'
import { data, Link, useLoaderData } from 'react-router'
import { AdSlot } from '#app/components/ads/ad-slot.tsx'
import { BusinessCard } from '#app/components/business-card.tsx'
import { FeedbackForm } from '#app/components/feedback/feedback-form.tsx'
import { RentalCard } from '#app/components/rental-card.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Alert, AlertDescription } from '#app/components/ui/alert.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { CopyButton } from '#app/components/ui/copy-button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.js'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { useCanonical } from '#app/seo/hooks.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildFaqLd, buildLocalBusinessLd } from '#app/seo/structured-data.ts'
import { getAdForLocation } from '#app/utils/ads.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { formatPhoneNumber, pluralize } from '#app/utils/misc.tsx'
import { type Route } from './+types/$businessSlug.ts'

export async function loader({ params }: Route.LoaderArgs) {
	const { businessSlug } = params

	const business = await prisma.business.findUnique({
		where: { slug: businessSlug, status: 'published' },
		select: {
			id: true,
			name: true,
			isFeatured: true,
			slug: true,
			description: true,
			address: true,
			phone: true,
			website: true,
			createdAt: true,
			ownerId: true,
			aiDescription: true,
			aiFaqSnippets: true,
			highlights: true,
			aiTopics: true,
			aiServices: true,
			featuredMedia: {
				select: {
					id: true,
					name: true,
					title: true,
					altText: true,
					url: true,
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			seoMeta: {
				select: {
					title: true,
					description: true,
					canonicalUrl: true,
					focusKeyword: true,
					seoKeywords: true,
					noIndex: true,
					ogTitle: true,
					ogDescription: true,
					ogImage: true,
					ogType: true,
					twitterTitle: true,
					twitterDescription: true,
					twitterImage: true,
					twitterCard: true,
					structuredData: true,
				},
			},
		},
	})

	if (!business) {
		throw new Response('Business not found', { status: 404 })
	}

	// Fetch related businesses
	const RELATED_LIMIT = 3

	const subcategoryIds = business.subcategories.map((s) => s.id)
	const categoryIds = business.categories.map((c) => c.id)

	const selectForCard = {
		id: true,
		name: true,
		slug: true,
		isFeatured: true,
		description: true,
		aiDescription: true,
		phone: true,
		website: true,
		address: true,
		categories: { select: { id: true, name: true, slug: true } },
		subcategories: { select: { id: true, name: true, slug: true } },
		cities: { select: { id: true, name: true, slug: true } },
	} as const

	const subMatches = subcategoryIds.length
		? await prisma.business.findMany({
				where: {
					id: { not: business.id },
					status: 'published',
					subcategories: { some: { id: { in: subcategoryIds } } },
				},
				select: selectForCard,
				take: 30,
			})
		: []

	const catMatches = categoryIds.length
		? await prisma.business.findMany({
				where: {
					id: { not: business.id },
					status: 'published',
					categories: { some: { id: { in: categoryIds } } },
				},
				select: selectForCard,
				take: 30,
			})
		: []

	function shuffleInPlace<T>(items: T[]) {
		for (let i = items.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1))
			const temp = items[i]
			const tempJ = items[j]
			if (temp !== undefined && tempJ !== undefined) {
				items[i] = tempJ
				items[j] = temp
			}
		}
	}

	const byId = new Map<string, (typeof subMatches)[number]>()
	for (const b of [...subMatches, ...catMatches])
		if (!byId.has(b.id)) byId.set(b.id, b)
	const candidates = Array.from(byId.values())

	const featured = candidates.filter((b) => b.isFeatured)
	const regular = candidates.filter((b) => !b.isFeatured)
	shuffleInPlace(featured)
	shuffleInPlace(regular)
	const relatedBusinesses = [...featured, ...regular].slice(0, RELATED_LIMIT)

	// Get ad for sidebar
	const ad = await getAdForLocation('BUSINESS_SIDEBAR_ABOVE_RELATED', {
		categories: business.categories,
		subcategories: business.subcategories,
		cities: business.cities,
		pageType: 'business',
		businessId: business.id,
	})

	// Fetch rental listings for this business
	const rentalListings = await prisma.rentalListing.findMany({
		where: {
			ownerBusinessId: business.id,
			status: 'published',
		},
		select: {
			id: true,
			title: true,
			slug: true,
			description: true,
			hourlyRateCents: true,
			dailyRateCents: true,
			weeklyRateCents: true,
			monthlyRateCents: true,
			featuredMedia: {
				select: {
					url: true,
					altText: true,
				},
			},
			categories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			subcategories: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			cities: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerBusiness: {
				select: {
					id: true,
					name: true,
					slug: true,
				},
			},
			ownerUser: {
				select: {
					id: true,
					name: true,
				},
			},
		},
		orderBy: {
			createdAt: 'desc',
		},
	})

	return data({ business, relatedBusinesses, ad, rentalListings })
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		const business = data?.business
		return business
			? { name: business.name, url: `/businesses/${business.slug}` }
			: null
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		params: Record<string, string>
		pathname: string
	}): SeoProps | null {
		const business = loaderData?.business
		if (!business) return null

		const seo = business.seoMeta
		const cityNames = business.cities.map((c) => c.name).join(', ')
		const canonicalUrl =
			seo?.canonicalUrl || `${origin}/businesses/${business.slug}`
		const defaultDescription =
			business.aiDescription ||
			business.description ||
			`Find ${business.name} in ${cityNames}. Contact information and details for local services.`

		// Build keywords from focus keyword, SEO keywords, and categories
		const keywords = [
			seo?.focusKeyword,
			...(seo?.seoKeywords || []),
			...business.categories.map((c) => c.name),
		].filter(Boolean)

		// Build structured data
		const structuredData: Array<object | string> = [
			buildLocalBusinessLd(business, canonicalUrl),
		]

		const faq = Array.isArray(business.aiFaqSnippets)
			? (business.aiFaqSnippets as unknown[]).filter(isFaqItem)
			: []
		if (faq.length > 0) structuredData.push(buildFaqLd(faq))

		// Append custom structured data from DB if present
		if (seo?.structuredData) {
			const customData = seo.structuredData
			if (Array.isArray(customData)) {
				structuredData.push(...(customData as Array<object | string>))
			} else if (customData !== null) {
				structuredData.push(customData as object | string)
			}
		}

		return {
			title: seo?.title || `${business.name} - ${cityNames} - RentDFW.com`,
			description: seo?.description || defaultDescription,
			canonical: canonicalUrl,
			keywords: keywords.length > 0 ? keywords : undefined,
			robots: seo?.noIndex ? 'noindex, nofollow' : undefined,
			openGraph: {
				title: seo?.ogTitle || business.name,
				description: seo?.ogDescription || defaultDescription,
				...(seo?.ogImage || business.featuredMedia?.url
					? {
							image: seo?.ogImage || business.featuredMedia?.url || undefined,
						}
					: {}),
				type: seo?.ogType || 'website',
				url: canonicalUrl,
			},
			twitter: {
				card:
					(seo?.twitterCard as 'summary_large_image' | 'summary') ||
					'summary_large_image',
				title: seo?.twitterTitle || business.name,
				description: seo?.twitterDescription || defaultDescription,
				...(seo?.twitterImage || business.featuredMedia?.url
					? {
							image:
								seo?.twitterImage || business.featuredMedia?.url || undefined,
						}
					: {}),
			},
			structuredData,
		}
	},
}

export default function BusinessPage() {
	const { business, relatedBusinesses, ad, rentalListings } =
		useLoaderData<typeof loader>()
	const canonicalUrl = useCanonical(`/businesses/${business.slug}`)
	const [flagDialogOpen, setFlagDialogOpen] = useState(false)

	// Highlights are now deterministic strings from attributes
	const highlights = (business.highlights as string[]) || []
	const topics = business.aiTopics || ''
	const services = Array.isArray(business.aiServices)
		? (business.aiServices as unknown[]).filter(isAiServiceItem)
		: []
	const faq = Array.isArray(business.aiFaqSnippets)
		? (business.aiFaqSnippets as unknown[]).filter(isFaqItem)
		: []

	// computed in handle.seo

	// Build structured data (breadcrumbs are auto-generated by layout)
	const structuredData = [buildLocalBusinessLd(business, canonicalUrl)]

	if (faq.length > 0) {
		structuredData.push(buildFaqLd(faq))
	}

	return (
		<>
			{/* SEO is handled by SeoHead via handle.seo */}
			<div className="container mx-auto px-4 py-16">
				<div className="grid gap-8 lg:grid-cols-3">
					{/* Main Content */}
					<div className="lg:col-span-2">
						<h1 className="mb-4 text-4xl font-bold">{business.name}</h1>

						<div className="text-muted-foreground mb-6 flex flex-col gap-2 text-sm md:flex-row md:items-center md:gap-6">
							<div className="flex items-center gap-2">
								<Icon name="map-pin" />
								{business.address && <p>{business.address}</p>}
							</div>

							{business.phone && (
								<div className="flex items-center gap-2">
									<Icon name="phone" />
									<a href={`tel:${business.phone}`} className="hover:underline">
										{formatPhoneNumber(business.phone)}
									</a>
								</div>
							)}

							{business.website && (
								<div className="flex items-center gap-2">
									<Icon name="link-2" />
									<a
										href={business.website}
										target="_blank"
										rel="noopener noreferrer"
										className="hover:underline"
									>
										Visit Website
									</a>
								</div>
							)}
						</div>

						{business.aiDescription && (
							<div className="mb-6">
								<p className="text-lg">{business.aiDescription}</p>
							</div>
						)}

						{business.description && !business.aiDescription && (
							<div className="mb-6">
								<p className="text-lg">{business.description}</p>
							</div>
						)}

						{business.featuredMedia && (
							<div className="mb-6">
								{business.website ? (
									<a
										href={business.website}
										target="_blank"
										rel="noopener noreferrer"
										className="group hover:border-ring hover:outline-border/50 relative block self-stretch overflow-clip rounded-md border outline-transparent transition-all duration-300 ease-out hover:outline-4"
									>
										<img
											src={business.featuredMedia.url || ''}
											alt={business.featuredMedia.altText || business.name}
											loading="lazy"
											decoding="async"
											className="aspect-video h-auto w-full object-cover object-top transition-transform duration-300 ease-out will-change-transform group-hover:scale-[101%]"
										/>
									</a>
								) : (
									<div className="relative overflow-clip rounded-md border">
										<img
											src={business.featuredMedia.url || ''}
											alt={business.featuredMedia.altText || business.name}
											loading="lazy"
											decoding="async"
											className="aspect-video h-auto w-full object-cover object-top"
										/>
									</div>
								)}
							</div>
						)}

						{topics && (
							<Alert variant="info" className="mb-6">
								<Icon name="check-circled" />
								<AlertDescription>{topics}</AlertDescription>
							</Alert>
						)}

						{services.length > 0 && (
							<div className="my-12">
								<h2 className="mb-6 text-xl font-bold">
									Services offered by {business.name}
								</h2>
								<dl className="grid grid-cols-1 gap-x-8 gap-y-8 lg:max-w-none lg:grid-cols-2">
									{services.map((service, idx) => (
										<div key={idx} className="relative flex flex-col pl-9">
											<dt className="text-foreground text-base font-semibold">
												<Icon
													name="handshake"
													className="absolute top-1 left-1 size-5"
												/>
												{service.service}
											</dt>
											{service.content && (
												<dd className="text-muted-foreground mt-1 flex flex-auto flex-col text-base">
													<p className="flex-auto leading-relaxed">
														{service.content}
													</p>
												</dd>
											)}
										</div>
									))}
								</dl>
							</div>
						)}

						{/* Highlights Section */}
						{highlights.length > 0 && (
							<div className="mb-12">
								<dl className="bg-muted/90 grid grid-cols-1 gap-0.5 overflow-hidden rounded-md text-center sm:grid-cols-2 lg:grid-cols-3">
									{highlights.map((highlight, idx) => (
										<div key={idx} className="bg-muted/90 flex flex-col p-6">
											<dd className="text-foreground text-sm leading-relaxed font-medium">
												<Icon
													name="check-circled"
													className="mr-2 size-4 text-green-500"
												/>
												{highlight}
											</dd>
										</div>
									))}
								</dl>
							</div>
						)}

						{/* Rental Listings Section */}
						{rentalListings.length > 0 && (
							<div className="mb-12">
								<div className="mb-6 flex items-center justify-between">
									<h2 className="text-xl font-bold">
										{rentalListings.length === 1
											? 'Rental Listing'
											: 'Rental Listings'}{' '}
										from {business.name}
									</h2>
									<span className="text-muted-foreground text-sm">
										{rentalListings.length}{' '}
										{rentalListings.length === 1 ? 'listing' : 'listings'}
									</span>
								</div>
								<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
									{rentalListings.map((rental) => (
										<RentalCard key={rental.id} rental={rental} />
									))}
								</div>
							</div>
						)}

						{faq.length > 0 && (
							<div className="mb-6">
								<h2 className="mb-4 text-lg font-bold">
									Frequently Asked Questions
								</h2>

								<Accordion type="single" collapsible className="w-full">
									{faq.map((item, idx) => (
										<AccordionItem key={idx} value={`item-${idx}`}>
											<AccordionTrigger>{item.q}</AccordionTrigger>
											<AccordionContent>
												<p className="text-muted-foreground text-sm leading-relaxed">
													{item.a}
												</p>
											</AccordionContent>
										</AccordionItem>
									))}
								</Accordion>
							</div>
						)}

						<div className="mb-6">
							<h3 className="text-muted-foreground mb-1 font-semibold">
								Categories
							</h3>
							<div className="flex flex-wrap gap-2">
								{business.categories.map((category) => (
									<Link
										key={category.id}
										to={`/businesses/category/${category.slug}`}
									>
										<Badge variant="outline" className="hover:bg-accent">
											{category.name}
										</Badge>
									</Link>
								))}
								{business.subcategories.map((subcategory) => (
									<Link
										key={subcategory.id}
										to={`/businesses/category/${subcategory.slug}`}
									>
										<Badge variant="outline" className="hover:bg-accent">
											{subcategory.name}
										</Badge>
									</Link>
								))}
							</div>
						</div>
						<div className="mb-6">
							<h3 className="text-muted-foreground mb-1 font-semibold">
								Cities
							</h3>
							<div className="flex flex-wrap gap-2">
								{business.cities.map((city) => (
									<Link key={city.id} to={`/businesses/city/${city.slug}`}>
										<Badge variant="outline" className="hover:bg-accent">
											{city.name}
										</Badge>
									</Link>
								))}
							</div>
						</div>

						<div className="mb-6 flex items-center justify-end gap-2">
							<CopyButton textToCopy={`${canonicalUrl}` || ''} />

							<Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
								<Tooltip>
									<TooltipTrigger asChild>
										<DialogTrigger asChild>
											<Button variant="outline" size="sm">
												<Icon name="flag" />
											</Button>
										</DialogTrigger>
									</TooltipTrigger>
									<TooltipContent>
										<p>Send report/suggestion</p>
									</TooltipContent>
								</Tooltip>

								<DialogContent>
									<DialogHeader>
										<DialogTitle>Report an Issue</DialogTitle>
										<DialogDescription>
											Help us improve by reporting issues with this business
											listing.
										</DialogDescription>
									</DialogHeader>
									<FeedbackForm
										variant="flag"
										defaultValues={{
											businessId: business.id,
											businessName: business.name,
											pageUrl: canonicalUrl,
										}}
										onSuccess={() => setFlagDialogOpen(false)}
									/>
								</DialogContent>
							</Dialog>

							{!business.ownerId && (
								<Button asChild variant="outline" size="sm">
									<Link to={`/claim?businessId=${business.id}`}>
										<Icon name="hand" />
										Claim
									</Link>
								</Button>
							)}
						</div>
					</div>

					{/* Sidebar */}
					<div className="mb-8 space-y-6 self-start md:sticky md:top-24">
						{/* Ad Slot */}
						{ad && (
							<AdSlot ad={ad} locationKey="BUSINESS_SIDEBAR_ABOVE_RELATED" />
						)}

						{/* Browse Related */}
						{business.cities.length > 0 &&
							(business.subcategories.length > 0 ||
								business.categories.length > 0) && (
								<Card>
									<CardHeader>
										<CardTitle>Related Rental Businesses</CardTitle>
									</CardHeader>
									<CardContent className="p-2">
										<ul role="list" className="divide-muted divide-y">
											{[...business.categories, ...business.subcategories].map(
												(category) =>
													business.cities.map((city) => (
														<li
															key={`${category.id}-${city.id}`}
															className="group relative"
														>
															<Link
																to={`/businesses/${city.slug}/${category.slug}`}
																className="flex items-center justify-between gap-x-6 py-4"
															>
																<div className="min-w-0 flex-auto">
																	<p className="text-foreground pl-4 text-sm font-semibold group-hover:underline">
																		{city.name} {category.name}
																	</p>
																</div>
																<div className="flex shrink-0 items-center pr-4">
																	<Icon
																		name="chevron-right"
																		className="text-muted-foreground size-5 transition-transform group-hover:translate-x-1"
																	/>
																</div>
															</Link>
														</li>
													)),
											)}
										</ul>
									</CardContent>
								</Card>
							)}

						{/* Claim Listing CTA */}
						{/* {!business.ownerId && (
							<Card>
								<CardHeader>
									<CardTitle>Own this business?</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-muted-foreground mb-4 text-sm">
										Claim this listing to manage your business information and
										connect with customers.
									</p>
									<Button
										asChild
										className="w-full"
										variant="outline"
										size="sm"
									>
										<Link to={`/claim?businessId=${business.id}`}>
											<Icon name="hand" />
											Claim this listing
										</Link>
									</Button>
								</CardContent>
							</Card>
						)} */}
					</div>
				</div>

				{relatedBusinesses.length > 0 && (
					<div>
						<div className="mb-3 flex items-center justify-between">
							<h3 className="font-bold">
								More local{' '}
								{pluralize(relatedBusinesses[0]?.categories[0]?.name || '')} in
								Dallas-Fort Worth
							</h3>
							<Button variant="outline" size="sm" asChild>
								<Link
									to={`/businesses/category/${relatedBusinesses[0]?.categories[0]?.slug}`}
								>
									View All <Icon name="chevron-right" />
								</Link>
							</Button>
						</div>
						<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
							{relatedBusinesses.map((rb) => (
								<BusinessCard key={rb.id} business={rb} />
							))}
						</div>
					</div>
				)}
			</div>
		</>
	)
}

interface AiService {
	service: string
	content: string
}

interface FaqItem {
	q: string
	a: string
}

function isAiServiceItem(value: unknown): value is AiService {
	if (!value || typeof value !== 'object') return false
	const v = value as Record<string, unknown>
	return typeof v.service === 'string' && typeof v.content === 'string'
}

function isFaqItem(value: unknown): value is FaqItem {
	if (!value || typeof value !== 'object') return false
	const v = value as Record<string, unknown>
	return typeof v.q === 'string' && typeof v.a === 'string'
}
